import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon56ErrorOutline } from '@vkontakte/icons';
import { ButtonGroup, Link, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import { differenceInDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BASEQUOTA } from 'reactApp/appHelpers/configHelpers';
import { overquotaNewUnsub } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { formatLocale } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './OverquotaWarningModal.css';

interface Props {
    subscription: BillingDataSubscriptionInfo;
    isCardUnlink?: boolean;
    bazSize?: number;
    onClose?: () => void;
    onCancel?: () => void;
    onSuccess?: () => void;
    onAction?: () => void;
}

const sendRadar = (action: string, name: string) => {
    sendDwh({
        label: `my-tariff-cancel-ovr-${action}`,
        eventCategory: 'payment',
        dwh_enabled: true,
        dwhData: { name },
    });
};

const sendCardUnlinkRadar = (action: string) => {
    const cardAction = action === 'save' || action === 'cancel' ? 'click' : action;
    const buttonName = action === 'save' ? 'keep_card' : action === 'cancel' ? 'unlink_card' : undefined;
    const platform = EnvironmentSelectors.isMobile() ? 'touch' : 'web';

    sendDwh({
        action: cardAction,
        eventCategory: 'modal',
        dwh_enabled: true,
        dwhData: {
            name: 'mnz_card_unlink_outflow_overquota_warning',
            platform,
            ...(buttonName ? { button_name: buttonName } : {}),
        },
    });
};

const messages = {
    title: 'Без подписки могут появиться ограничения',
    // prettier-ignore
    descriptionExtra: <>{'Удалите лишние файлы в '}<Link href={overquotaNewUnsub.cloudURL}>Облаке</Link>{' или письма в '}<Link href={overquotaNewUnsub.mailURL}>Почте</Link>{'. Либо сохраните подписку Mail Space, чтобы избежать ограничений'}</>,
    buttonPrimary: 'Сохранить подписку',
    buttonSecondary: 'Отключить всё равно',
};

const cardUnlinkMessages = {
    title: 'Без карты могут появиться ограничения',
    // prettier-ignore
    descriptionExtra: <>{'Удалите лишнее в '}<Link href={overquotaNewUnsub.cloudURL}>Облаке</Link>{' или в '}<Link href={overquotaNewUnsub.mailURL}>Почте</Link>{'. Либо оставьте карту и сохраните подписку Mail Space'}</>,
    buttonPrimary: 'Оставить карту',
    buttonSecondary: 'Всё равно отвязать',
};

export const OverquotaWarningModal = (props: Props) => {
    const { isCardUnlink, bazSize = 0, onCancel, onClose: onCloseProp, subscription } = props;
    const { expires } = subscription;
    const currentMessages = isCardUnlink ? cardUnlinkMessages : messages;
    const usedQuota = useSelector(UserQuotaSelectors.getUsed).total;
    const overflow = bytesToNDigits(usedQuota ? usedQuota.original + (isCardUnlink ? bazSize : 0) - BASEQUOTA : 0, 3);

    const endsSoon = differenceInDays(expires, new Date()) <= 7;
    const radarName = endsSoon ? 'few_days_before_block' : 'many_days_before_block';

    const isMobile = EnvironmentSelectors.isMobile();
    const ref = useRef<HTMLDivElement>(null);

    const onClick = useCallback(() => {
        onCloseProp?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('save');
        } else {
            sendRadar('save', radarName);
        }
    }, [isCardUnlink, onCloseProp, radarName]);

    const onClose = useCallback(() => {
        onCloseProp?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('close');
        } else {
            sendRadar('close', radarName);
        }
    }, [isCardUnlink, onCloseProp, radarName]);

    const onSecondaryClick = useCallback(() => {
        onCancel?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('cancel');
        } else {
            sendRadar('cancel', radarName);
        }
    }, [isCardUnlink, onCancel, radarName]);

    useEffect(() => {
        if (isCardUnlink) {
            sendCardUnlinkRadar('show');
        } else {
            sendRadar('show', radarName);
        }
    }, [isCardUnlink, radarName]);

    const getDescription = useCallback(() => {
        if (isCardUnlink) {
            return (
                <>
                    <Text className={styles.text}>
                        {'В\u00A0едином пространстве на\u00A0'}
                        <span className={styles.negative}>{overflow.value}</span>
                        {' больше файлов и\u00A0писем сверх\u00A0бесплатных 8\u00A0ГБ'}
                    </Text>
                    <Text className={styles.text}>
                        {'Без\u00A0карты подписка не\u00A0продлится, поэтому\u00A0с\u00A0'}
                        <span className={styles.negative}>{formatLocale(expires, 'd MMMM')}</span> нельзя будет:
                    </Text>
                    <ul className={styles.list}>
                        <li className={styles.text}>{'смотреть, загружать и\u00A0отправлять файлы в\u00A0Облаке'}</li>
                        <li className={styles.text}>{'отправлять и\u00A0читать новые письма в\u00A0Почте'}</li>
                    </ul>
                    <Text className={styles.text}>
                        {'Подробнее в\u00A0'}
                        <Link href={overquotaNewUnsub.helpURL}>статье</Link>
                    </Text>
                </>
            );
        }

        if (endsSoon) {
            return (
                <>
                    <Text className={styles.text}>
                        В едином пространстве на <span className={styles.negative}>{overflow.value}</span> больше файлов и писем сверх
                        бесплатных 8 ГБ
                    </Text>
                    <Text className={styles.text}>
                        Поэтому с <span className={styles.negative}>{formatLocale(expires, 'd MMMM')}</span> нельзя будет:
                    </Text>
                    <ul className={styles.list}>
                        <li className={styles.text}>{'смотреть, загружать и отправлять файлы в Облаке'}</li>
                        <li className={styles.text}>{'отправлять и читать новые письма в Почте'}</li>
                    </ul>
                    <Text className={styles.text}>
                        Подробнее в <Link href={overquotaNewUnsub.helpURL}>статье</Link>
                    </Text>
                </>
            );
        }
        return (
            <>
                <Text className={styles.text}>{'Если пространство заполнится сверх 8 бесплатных ГБ, когда отключится Mail Space:'}</Text>
                <ul className={styles.list}>
                    <li className={styles.text}>{'нельзя будет смотреть, загружать и отправлять файлы в Облаке'}</li>
                    <li className={styles.text}>
                        {'не получится отправлять и читать новые письма в Почте. '}
                        <Link href={overquotaNewUnsub.helpURL}>Подробнее</Link>
                    </li>
                </ul>
            </>
        );
    }, [endsSoon, expires, isCardUnlink, overflow]);

    const buttons = useMemo(() => {
        return (
            <ButtonGroup mode={isMobile ? 'vertical' : 'horizontal'} gap={'s'} stretched={isMobile}>
                <Button
                    qaId={'modal-primary-btn'}
                    theme={'vk'}
                    primary
                    onClick={onClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {currentMessages.buttonPrimary}
                </Button>
                <Button
                    qaId={'modal-secondary-btn'}
                    theme={'vk'}
                    secondary
                    onClick={onSecondaryClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {currentMessages.buttonSecondary}
                </Button>
            </ButtonGroup>
        );
    }, [currentMessages.buttonPrimary, currentMessages.buttonSecondary, isMobile, onClick, onSecondaryClick]);

    const content = useMemo(() => {
        return (
            <>
                <div className={classNames(styles.upper_image, { [styles.mobile]: isMobile })}>
                    <Spacing size={isMobile ? 16 : 48} />
                    <Icon56ErrorOutline />
                </div>
                <Spacing size={12} />
                <Text className={isMobile ? styles.title_mobile : styles.title}>{currentMessages.title}</Text>
                <Spacing size={16} />
                {getDescription()}
                <Spacing size={24} />
                <Text className={styles.text}>{currentMessages.descriptionExtra}</Text>
                <Spacing size={isMobile ? 12 : 36} />
                {!isMobile && (
                    <>
                        {buttons}
                        <Spacing size={32} />
                    </>
                )}
            </>
        );
    }, [buttons, currentMessages.descriptionExtra, currentMessages.title, getDescription, isMobile]);

    if (isMobile) {
        return (
            <MobileDialog
                id={'overquota-warning-modal'}
                open
                onClose={onClose}
                closable
                mod={'confirm'}
                topmost
                scrollRef={ref}
                isRebrandingQuotaLanding
                dimmer
                closeOnDimmerClick
                title={<></>}
                footer={buttons}
                className={styles.card}
            >
                <div ref={ref} className={styles.card}>
                    {content}
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            id={'overquota-warning-modal'}
            onCancel={onClose}
            closable
            closeOnEscape={true}
            closeOnDimmerClick={false}
            className={styles.card}
            size={'tiny'}
            dimmer={false}
        >
            <div ref={ref} className={styles.card}>
                {content}
            </div>
        </Dialog>
    );
};

export const openOverquotaWarningModal = (data?: Props) =>
    openPopupHelper({ popupName: popupNames.OVERQUOTA_WARNING_MODAL, data, onClose: data?.onClose });

export const closeOverquotaWarningModal = () => closePopupHelper(popupNames.OVERQUOTA_WARNING_MODAL);
